import { useEffect, useId, useRef, useState } from 'react'
import { krakenAPI, krakenError, krakenErrorCode } from '../../services/krakenApi'
import type { KrakenTicket, LiveTrade, TicketCheck } from '../../types/kraken'
import useEscapeKey from '../../hooks/useEscapeKey'
import { formatPrice } from '../../utils/marketPrice'
import Spinner from '../Spinner'
import { LiveTradeStatusBadge, usd } from './tradeFormat'

type Step = 'edit' | 'validated' | 'submitting' | 'result'

const PREVIEW_DEBOUNCE_MS = 700
const DEFAULT_USD = 25

interface TradeTicketModalProps {
  signalId: number
  onClose: () => void
  /** Called once Kraken has answered a real submission (whatever it said). */
  onSubmitted?: (trade: LiveTrade) => void
}

/**
 * Review Trade → Validate Order → Confirm and place order.
 *
 * Nothing here decides anything: every number and every check comes from the
 * server, which re-runs all of them again at confirm time. The ticket only
 * shows success once Kraken itself has reported an order id, and it reports
 * whatever state Kraken gave back — open, partially filled, filled, rejected.
 */
const TradeTicketModal = ({ signalId, onClose, onSubmitted }: TradeTicketModalProps) => {
  const titleId = useId()
  const [step, setStep] = useState<Step>('edit')
  const [amount, setAmount] = useState(String(DEFAULT_USD))
  const [ticket, setTicket] = useState<KrakenTicket | null>(null)
  const [validated, setValidated] = useState<KrakenTicket | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<LiveTrade | null>(null)
  const [maxOrder, setMaxOrder] = useState<number | null>(null)
  const request = useRef(0)

  // Submitting cannot be dismissed: the answer must be seen.
  useEscapeKey(onClose, step !== 'submitting')

  useEffect(() => {
    krakenAPI
      .access()
      .then((a) => setMaxOrder(a.max_order_usd ?? null))
      .catch(() => undefined)
  }, [])

  const usdAmount = Number(amount)
  const amountValid = Number.isFinite(usdAmount) && usdAmount > 0

  // Live preview: numbers and read-only pre-checks, debounced as the amount changes.
  useEffect(() => {
    if (step !== 'edit' || !amountValid) return
    const id = ++request.current
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const t = await krakenAPI.ticket(signalId, usdAmount)
        if (id === request.current) {
          setTicket(t)
          setError(null)
        }
      } catch (e) {
        if (id === request.current) setError(krakenError(e, 'Could not load the ticket.'))
      } finally {
        if (id === request.current) setLoading(false)
      }
    }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [signalId, usdAmount, amountValid, step])

  const validate = async () => {
    setLoading(true)
    setError(null)
    try {
      const v = await krakenAPI.validate(signalId, usdAmount)
      setValidated(v)
      setTicket(v)
      setStep('validated')
    } catch (e) {
      setError(krakenError(e, 'Validation failed.'))
    } finally {
      setLoading(false)
    }
  }

  const confirm = async () => {
    if (!validated?.trade_id) return
    setStep('submitting')
    setError(null)
    try {
      const trade = await krakenAPI.confirm(validated.trade_id)
      setResult(trade)
      onSubmitted?.(trade)
      setStep('result')
    } catch (e) {
      const code = krakenErrorCode(e)
      setError(
        code === 'kraken_unreachable'
          ? krakenError(e, 'Kraken did not answer.') +
              ' Check Trades → Pending orders before trying again: the order may exist.'
          : krakenError(e, 'The order was not sent.')
      )
      setStep('result')
    }
  }

  const t = ticket

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[95vh] w-full max-w-lg overflow-y-auto rounded-t-xl border border-terminal-border bg-terminal-surface p-4 text-gray-100 sm:rounded-xl sm:p-6"
        data-testid="trade-ticket"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id={titleId} className="text-lg font-bold text-gray-50">
              Review Trade
            </h2>
            <p className="text-xs text-gray-400">
              Kraken Spot · limit order · confirm first
              {t ? ` · signal #${t.signal_id}` : ''}
            </p>
          </div>
          {step !== 'submitting' && (
            <button
              type="button"
              onClick={onClose}
              className="rounded px-2 py-1 text-sm text-gray-400 hover:bg-white/5 hover:text-gray-100"
              aria-label="Close"
            >
              ✕
            </button>
          )}
        </div>

        {step === 'submitting' && (
          <div className="flex flex-col items-center gap-3 py-12 text-center" role="status">
            <Spinner />
            <p className="font-semibold text-gray-100">Submitting order to Kraken…</p>
            <p className="text-xs text-gray-400">Waiting for Kraken's answer. Don't close this window.</p>
          </div>
        )}

        {step === 'result' && (
          <SubmitResult trade={result} error={error} onClose={onClose} />
        )}

        {(step === 'edit' || step === 'validated') && (
          <>
            {t && <TicketSummary ticket={t} />}

            {step === 'edit' && (
              <div className="mt-4">
                <label htmlFor="ticket-usd" className="text-xs font-medium uppercase tracking-wider text-gray-400">
                  Amount (USD)
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-gray-400">$</span>
                  <input
                    id="ticket-usd"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full rounded-md border border-terminal-border bg-terminal-bg px-3 py-2 font-mono text-gray-50 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                {maxOrder != null && (
                  <p className="mt-1 text-xs text-gray-500">Hard cap per order: {usd(maxOrder)} (incl. entry fee)</p>
                )}
              </div>
            )}

            {t?.insufficient_balance && (
              <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm" data-testid="insufficient-balance">
                <p className="font-semibold text-red-200">Insufficient balance — add funds on Kraken</p>
                <p className="mt-1 text-xs text-gray-300">
                  Available {usd(t.usd_available ?? 0)} · needed {usd(t.notional + (t.notional * t.taker_fee_pct) / 100)}
                </p>
                <a
                  href={t.deposit_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-sm font-semibold text-primary-400 hover:text-primary-300"
                >
                  Deposit on Kraken →
                </a>
              </div>
            )}

            {t && <Checklist checks={t.checks} />}

            {error && (
              <p className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 p-2 text-sm text-red-200" role="alert">
                {error}
              </p>
            )}

            <p className="mt-4 text-center text-sm font-semibold text-amber-300" data-testid="no-trade-yet">
              No trade has been placed yet.
            </p>

            {step === 'edit' ? (
              <button
                type="button"
                onClick={validate}
                disabled={loading || !amountValid}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading && <Spinner size="sm" className="border-white border-t-transparent" />}
                Validate Order
              </button>
            ) : (
              <ConfirmBlock
                canConfirm={Boolean(validated?.can_confirm)}
                onConfirm={confirm}
                onBack={() => {
                  setStep('edit')
                  setValidated(null)
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

const Row = ({ label, value, valueClass = 'text-gray-100' }: { label: string; value: string; valueClass?: string }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <dt className="text-xs text-gray-400">{label}</dt>
    <dd className={`text-right font-mono text-sm tabular-nums ${valueClass}`}>{value}</dd>
  </div>
)

const price = (value: number | null | undefined) => (value == null ? '—' : `$${formatPrice(value)}`)

const TicketSummary = ({ ticket: t }: { ticket: KrakenTicket }) => (
  <div>
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span className="text-xl font-bold text-gray-50">{t.symbol}</span>
      <span
        className={`rounded px-2 py-0.5 text-xs font-bold ${
          t.direction === 'long' ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'
        }`}
      >
        {t.direction.toUpperCase()}
      </span>
      <span className="rounded bg-white/5 px-2 py-0.5 text-xs text-gray-300">{t.order_type.toUpperCase()}</span>
      {t.kraken_pair && <span className="text-xs text-gray-500">{t.kraken_pair}</span>}
    </div>
    <dl className="divide-y divide-terminal-border rounded-md border border-terminal-border bg-terminal-bg px-3">
      <Row label="Live Kraken price" value={price(t.live_price)} />
      <Row label="Signal entry" value={price(t.signal_entry)} />
      <Row label="Limit price" value={price(t.limit_price)} />
      <Row label="Estimated quantity" value={t.quantity ? t.quantity.toString() : '—'} />
      <Row label="Take-profit" value={price(t.take_profit)} valueClass="text-green-300" />
      <Row label="Stop" value={price(t.stop_loss)} valueClass="text-red-300" />
      <Row label="Max planned loss" value={`−${usd(t.max_loss)}`} valueClass="text-red-300" />
      <Row label="Planned profit" value={`+${usd(t.planned_profit)}`} valueClass="text-green-300" />
      <Row
        label={`Estimated fees (${t.taker_fee_pct}%/side${t.fees_estimated ? ', estimate' : ''})`}
        value={usd(t.est_fees)}
      />
      <Row
        label="Signal expiry"
        value={t.signal_expires_at ? new Date(t.signal_expires_at).toLocaleString() : '—'}
      />
      <Row label="Signal ID" value={`#${t.signal_id}`} />
    </dl>
    <p className="mt-2 text-[11px] leading-snug text-gray-500">
      Max loss assumes the stop fills at its price; a fast market can fill it lower. The stop rests on Kraken once
      the entry fills; FinCreator sells at the target when Kraken's price reaches it.
    </p>
  </div>
)

const Checklist = ({ checks }: { checks: TicketCheck[] }) => (
  <ul className="mt-4 space-y-1.5" data-testid="checklist">
    {checks.map((c) => (
      <li key={c.key} className="flex items-start gap-2 text-sm" data-ok={c.ok}>
        <span aria-hidden="true" className={`mt-0.5 font-bold ${c.ok ? 'text-green-400' : 'text-red-400'}`}>
          {c.ok ? '✓' : '✗'}
        </span>
        <span>
          <span className={c.ok ? 'text-gray-200' : 'font-semibold text-red-200'}>{c.label}</span>
          {c.detail && <span className="block text-xs text-gray-500">{c.detail}</span>}
        </span>
      </li>
    ))}
  </ul>
)

const ConfirmBlock = ({
  canConfirm,
  onConfirm,
  onBack,
}: {
  canConfirm: boolean
  onConfirm: () => void
  onBack: () => void
}) => (
  <div className="mt-3 space-y-3">
    {canConfirm ? (
      <div className="rounded-md border-2 border-red-500 bg-red-500/10 p-3" role="alert" data-testid="real-money-warning">
        <p className="font-bold text-red-200">⚠ Real money</p>
        <p className="mt-1 text-sm text-red-100">
          You are authorizing FinCreator to submit a real Kraken order. This trade can lose money.
        </p>
      </div>
    ) : (
      <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
        This order cannot be confirmed: fix the failed checks above.
      </p>
    )}
    <div className="flex flex-col-reverse gap-2 sm:flex-row">
      <button
        type="button"
        onClick={onBack}
        className="flex-1 rounded-md border border-terminal-border px-4 py-2.5 text-sm font-medium text-gray-300 hover:bg-white/5"
      >
        Back
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={!canConfirm}
        className="flex-1 rounded-md bg-red-600 px-4 py-2.5 font-bold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Confirm and place order
      </button>
    </div>
  </div>
)

const RESULT_TEXT: Partial<Record<LiveTrade['status'], string>> = {
  pending_fill: 'Kraken accepted the order. It is on the book waiting to fill — no position yet.',
  partially_filled: 'Kraken filled part of the order. The rest is still on the book.',
  open: 'Kraken filled the order. The stop is placed on Kraken next.',
  rejected: 'Kraken rejected the order. Nothing was placed.',
  unknown:
    'Kraken did not answer, so it is not known yet whether the order exists. FinCreator is checking Kraken — see Trades → Pending orders.',
  expired_unfilled: 'The order expired without filling. No position was opened.',
  cancelled_unfilled: 'The order was cancelled without filling. No position was opened.',
}

const SubmitResult = ({ trade, error, onClose }: { trade: LiveTrade | null; error: string | null; onClose: () => void }) => (
  <div className="space-y-4 py-2" data-testid="submit-result">
    {trade ? (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-400">Kraken says:</span>
          <LiveTradeStatusBadge trade={trade} />
        </div>
        <p className="text-sm text-gray-200">{RESULT_TEXT[trade.status] ?? trade.status_detail}</p>
        {trade.kraken_error && (
          <p className="rounded-md border border-red-500/40 bg-red-500/10 p-2 font-mono text-sm text-red-200">
            {trade.kraken_error}
          </p>
        )}
        <dl className="rounded-md border border-terminal-border bg-terminal-bg px-3">
          <Row label="Kraken order ID" value={trade.entry_txid ?? '— (none)'} />
          <Row label="Filled" value={`${trade.filled_qty} of ${trade.requested_qty}`} />
          {trade.avg_entry_price != null && <Row label="Average fill" value={price(trade.avg_entry_price)} />}
          {trade.entry_fee > 0 && <Row label="Fee" value={usd(trade.entry_fee)} />}
        </dl>
      </>
    ) : (
      <p className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200" role="alert">
        {error}
      </p>
    )}
    <button
      type="button"
      onClick={onClose}
      className="w-full rounded-md border border-terminal-border px-4 py-2.5 text-sm font-medium text-gray-200 hover:bg-white/5"
    >
      Done
    </button>
  </div>
)

export default TradeTicketModal
